import type {
  ConsentCategory,
  ConsentProvider,
  ConsentSnapshot,
  ConsentTool,
} from '@/Integrations/Consent/types';

function hasConsent(snapshot: ConsentSnapshot, category: ConsentCategory): boolean {
  return snapshot[category] === true;
}

function canRunTool(snapshot: ConsentSnapshot, tool: ConsentTool): boolean {
  if (!tool.requiredConsent.length) {
    return true;
  }

  return tool.requiredConsent.every((category) => hasConsent(snapshot, category));
}

export class ConsentOrchestrator {
  private readonly provider: ConsentProvider;

  private readonly toolLoaded = new Set<string>();

  private readonly toolMap = new Map<string, ConsentTool>();

  private unsubscribeProvider?: () => void;

  private syncChain: Promise<void> = Promise.resolve();

  constructor(provider: ConsentProvider, tools: ConsentTool[]) {
    this.provider = provider;
    tools.forEach((tool) => {
      this.toolMap.set(tool.id, tool);
    });
  }

  async start(): Promise<void> {
    await this.provider.init();
    await this.enqueueSync(this.provider.getSnapshot());

    this.unsubscribeProvider = this.provider.subscribe((snapshot) => {
      this.enqueueSync(snapshot).catch((error) => {
        console.error('[Consent] Failed to synchronize tools after consent change', error);
      });
    });
  }

  stop(): void {
    this.unsubscribeProvider?.();
  }

  private enqueueSync(snapshot: ConsentSnapshot): Promise<void> {
    this.syncChain = this.syncChain.then(async () => {
      const actions: Promise<void>[] = [];

      this.toolMap.forEach((tool, toolId) => {
        const shouldLoad = canRunTool(snapshot, tool);
        const isLoaded = this.toolLoaded.has(toolId);

        if (shouldLoad && !isLoaded) {
          actions.push(
            Promise.resolve(tool.load())
              .then(() => {
                this.toolLoaded.add(toolId);
              })
              .catch((error) => {
                console.warn(`[Consent] Failed to load tool "${toolId}"`, error);
              }),
          );
          return;
        }

        if (!shouldLoad && isLoaded && tool.unload) {
          actions.push(
            Promise.resolve(tool.unload())
              .then(() => {
                this.toolLoaded.delete(toolId);
              })
              .catch((error) => {
                console.warn(`[Consent] Failed to unload tool "${toolId}"`, error);
              }),
          );
          return;
        }

        if (!shouldLoad && isLoaded && !tool.unload) {
          this.toolLoaded.delete(toolId);
        }
      });

      await Promise.all(actions);
    });

    return this.syncChain;
  }
}
